import { hasValue } from 'utils/values';

/**
 * Return a function that calculates the width of the given string and caches the result in a closure.
 * The calculation is determined by the given `getRenderedWidth` function, which is only executed when the size is not cached.
 * `groupSize` and `sizeTolerance` set the range of the cache to prevent excessive re-calculation.
 */
export function createStringWidthCalculator(
  /** Get the DOM size, e.g. offsetWidth. */
  getRenderedWidth: (text: string) => number,
  /** The size of the group */
  groupSize = 5,
  /** The tolerance of the size */
  sizeTolerance = 2
) {
  const exactSizeCache: { [key: string]: number } = {};
  const sizeRangeCache: { [key: number]: number } = {};

  return (text: string) => {
    // Return the cached value if the exact match exists
    const cachedValue = exactSizeCache[text];
    if (hasValue(cachedValue)) {
      return cachedValue;
    }

    const stringLength = text.length;

    // Calculate the lower bound of the length group.
    const lowerSizeGroup = Math.ceil((stringLength - sizeTolerance) / groupSize) * groupSize;
    // Return the cached value if the length group exists
    if (hasValue(sizeRangeCache[lowerSizeGroup])) {
      return sizeRangeCache[lowerSizeGroup];
    }

    // No cache found, calculate the size and cache it.
    // Note: this is the expensive operation.
    const size = Math.round(getRenderedWidth(text));

    // Cache the size for the exact text.
    exactSizeCache[text] = size;

    // Calculate the upper bound of the length group.
    const upperSizeGroup = Math.ceil(stringLength / groupSize) * groupSize;
    // Cache the size for the upper bound of the length group.
    if (!hasValue(sizeRangeCache[upperSizeGroup])) {
      sizeRangeCache[upperSizeGroup] = size;
    }

    return size;
  };
}
